/**
 * Contacts somebody else's system reported, and the health of the feeds that
 * carried them.
 *
 * Three shapes here are load-bearing rather than incidental.
 *
 * **`source` is an object on this endpoint, not a string.** `Track.to_wire`
 * writes `source: "external"` and `routers/external.py` then overwrites the key
 * with the provenance record. A caller that types it as the `TrackSource` enum
 * compiles and then reads `undefined` for every field the operator needs. It is
 * typed as the record here so that cannot happen.
 *
 * **`observedAt` and `receivedAt` are both kept, and never collapsed.** Their
 * clock and ours are different claims: a source hours out of skew would
 * otherwise make every one of its contacts either permanently fresh or
 * permanently stale, silently, in opposite directions.
 *
 * **`sourceConfidence`-style values are never normalised.** Nothing in this
 * module turns one vendor's number into a cross-source score, because two
 * vendors' "0.8" do not mean the same thing and a comparison that invents
 * comparability is worse than no comparison at all.
 *
 * Where a feed listens is not here and cannot be: the server exposes no route
 * that accepts a URL, a host, a port or a credential, so there is nothing in
 * these types to redact. The operator's control is the stop switch.
 */

import { api, type ApiResult } from '@/api/client';

/** The four that can be acted on. UNKNOWN is never treated as friendly. */
export type Affiliation = 'friend' | 'hostile' | 'neutral' | 'unknown';

export type Dimension = 'air' | 'ground' | 'surface' | 'subsurface' | 'unknown';

/** Provenance, not classification: how we know a thing is there. */
export type TrackSourceKind = 'own-drone' | 'adsb' | 'detection' | 'manual' | 'external';

/** Who told us about one contact, and when they said it versus when it landed. */
export interface TrackProvenance {
  sourceId: string;
  /** Their id for the thing, verbatim. Our track id is derived from it. */
  sourceTrackId: string;
  adapterKind: string;
  /** Their claim about when this was true. null when they did not say. */
  observedAt: number | null;
  /** Our clock, when the bytes arrived. The one timestamp nobody can lie about. */
  receivedAt: number;
  /** Their type string, kept verbatim. */
  sourceType: string;
  /** Their derivation code (CoT `how`), kept verbatim. */
  how: string;
  /** They said this reached them from somewhere else. Two hops from the truth. */
  relayed: boolean;
  /**
   * Their own confidence, on their own scale, when the feed carries one.
   *
   * Optional because `routers/external.py` does not put it on this response
   * today — `FieldProvenance` holds it, the track wire does not. Absent must
   * therefore render as "none carried by this feed" and never as a substitute
   * figure: a normalised stand-in would be exactly the cross-source score §4
   * forbids, since two vendors' "0.8" do not mean the same thing.
   */
  sourceConfidence?: number | null;
  /**
   * The feed itself is down. A fact about the cable, not about this contact —
   * the difference between one object going quiet and two hundred of them
   * appearing to vanish at once.
   */
  offline: boolean;
}

/**
 * One external contact as the map draws it.
 *
 * Every optional value is `| null` rather than optional: a missing altitude and
 * an altitude of zero are different facts, and a caller cannot forget to handle
 * a field the type forces it to look at.
 */
export interface ExternalTrack {
  trackId: string;
  callsign: string;
  lat: number;
  lon: number;
  altM: number | null;
  courseDeg: number | null;
  speedMs: number | null;
  /** null reads as stale, never as fresh. */
  observedAt: number | null;
  affiliation: Affiliation;
  dimension: Dimension;
  staleAfterS: number;
  positionErrorM: number | null;
  altitudeErrorM: number | null;
  remarks: string;
  /** Computed by the server so two screens cannot disagree about it. */
  stale: boolean;
  ageS: number | null;
  /** The provenance record — NOT the `TrackSource` string. See the module note. */
  source: TrackProvenance;
  /**
   * What kind of knowledge this is: a partner C2's considered picture, a
   * transponder, or a camera's guess. Its own field because `source` is the
   * provenance record — the server sends both, and they answer different
   * questions: who told us, and how they came to know.
   *
   * Optional because a deployment can be mid-upgrade; absent reads as
   * `'external'`, the weaker of the claims.
   */
  trackSource?: TrackSourceKind;
  /**
   * The merged object this report was folded into on the map, or null.
   *
   * The rows on this endpoint stay one-per-report on purpose — it is the view
   * an operator audits the merged picture against. This field is how a marker
   * says "the picture believes I am the same aircraft as that one", so nobody
   * spends time reconciling a duplicate the system already reconciled and
   * never mentioned. Null is the common case.
   */
  mergedInto: string | null;
}

/**
 * Two feeds that coincide in space and time and disagree about what they are
 * looking at.
 *
 * Reported rather than resolved, and that is the whole value: picking the
 * more trusted one would produce a single confident marker built out of a
 * contradiction, and destroy the one fact worth having — that somebody is
 * wrong about a thing in the air.
 */
export interface TrackContradiction {
  /** Which claim they disagree on: `affiliation` or `dimension`. */
  field: string;
  aTrackId: string;
  bTrackId: string;
  aSourceId: string;
  bSourceId: string;
  aValue: string;
  bValue: string;
  /** How far apart the two reports put it. Small, or they would not clash. */
  separationM: number;
}

export interface ExternalTracksResponse {
  generatedAt: number;
  tracks: ExternalTrack[];
  /** False when no ingest is configured on this server at all. */
  configured: boolean;
  /**
   * Disagreements the correlator refused to resolve. Absent on a server that
   * predates them, so callers must tolerate undefined rather than assuming an
   * empty list means "no conflicts".
   */
  contradictions?: TrackContradiction[];
}

/** Whether a feed is up, as a fact with a time on it. */
export interface SourceHealth {
  /** `live` or `offline`. Anything that is not `live` is a dead feed. */
  state: string;
  lastSeenAt: number | null;
  lastError: string;
  lastErrorAt: number | null;
  updatedAt: number;
}

/** What the supervisor knows about the connection. Carries no endpoint. */
export interface SourceRuntime {
  kind: string;
  enabled: boolean;
  connected: boolean;
  failures: number;
  backoffS: number;
  restarts: number;
  startedAt: number | null;
  lastError: string;
  lastErrorAt: number | null;
}

/**
 * Everything that was refused, and why.
 *
 * A drop nobody can see is the failure this whole surface exists against: a
 * feed silently discarding events looks exactly like a quiet battlespace, and
 * an operator planning around a picture with holes in it is worse off than one
 * who can read "4,812 refused" and switch it off.
 */
export interface SourceCounters {
  bytesReceived: number;
  payloadsReceived: number;
  payloadsRateLimited: number;
  payloadsDropped: number;
  observationsParsed: number;
  observationsRefused: number;
  observationsWritten: number;
  tracksRefusedOverCap: number;
  lastEventAt: number | null;
  lastRefusal: string;
}

export interface ExternalSource {
  sourceId: string;
  name: string;
  kind: string;
  enabled: boolean;
  trust: number;
  /** False means this feed's `a-f-*` events land as UNKNOWN, however loud. */
  mayAssertFriend: boolean;
  mayAssertIdentity: boolean;
  staleAfterS: number;
  positionAccuracyDefaultM: number;
  uidNamespace: string;
  relay: boolean;
  health: SourceHealth | null;
  runtime: SourceRuntime | null;
  counters: SourceCounters | null;
}

export interface ExternalSourcesResponse {
  sources: ExternalSource[];
  configured: boolean;
}

/**
 * The live external picture. Polled, so `logged: false` — a refresh every few
 * seconds would push every real command out of the activity timeline.
 */
export function getExternalTracks(): Promise<ApiResult<ExternalTracksResponse>> {
  return api.get<ExternalTracksResponse>('/external/tracks', { logged: false });
}

/**
 * Say that these reports are not one object, and have it stay said.
 *
 * The decision is stored server-side and fed back into every later
 * correlation pass. It has to be: correlation is deterministic and re-runs on
 * every poll, so a split applied only to one response would be undone within
 * seconds and the operator would learn the control does nothing.
 *
 * Every pair among the ids is separated, so splitting a three-way merge leaves
 * three contacts. Operator role; the server refuses a viewer.
 */
export function splitCorrelation(
  trackIds: string[],
  reason = '',
): Promise<ApiResult<{ separated: number; trackIds: string[] }>> {
  return api.post<{ separated: number; trackIds: string[] }>(
    '/external/correlation/split',
    { trackIds, reason },
    { label: `SPLIT ${trackIds.length} CONTACTS` },
  );
}

/**
 * Say that these reports ARE one object, whatever the picture concluded.
 *
 * The inverse of {@link splitCorrelation} and the more dangerous direction: a
 * split adds a marker, a pin removes one, so a wrong pin deletes a contact
 * rather than duplicating it. It exists because the cases where it is right
 * are ones no data would settle — a mis-programmed transponder puts two ICAO
 * addresses on one airframe.
 *
 * A pin overrules geometry, a strong-id mismatch, and one source asserting two
 * objects. It does NOT overrule a contradiction: two feeds disagreeing about
 * whether the object is friendly still produce two markers, because a merged
 * track has to assert one affiliation and the server will not choose.
 */
export function pinCorrelation(
  trackIds: string[],
  reason = '',
): Promise<ApiResult<{ pinned: number; trackIds: string[] }>> {
  return api.post<{ pinned: number; trackIds: string[] }>(
    '/external/correlation/pin',
    { trackIds, reason },
    { label: `PIN ${trackIds.length} CONTACTS` },
  );
}

export function getExternalSources(): Promise<ApiResult<ExternalSourcesResponse>> {
  return api.get<ExternalSourcesResponse>('/external/sources', { logged: false });
}

/**
 * Cut or resume one feed. Operator-gated on the server; the UI gates the
 * control too, which is an affordance and not the enforcement.
 *
 * Logged deliberately, and with the source named: this is one of the decisions
 * a human has to answer for afterwards.
 */
export function setExternalSourceEnabled(
  sourceId: string,
  enabled: boolean,
): Promise<ApiResult<{ sourceId: string; enabled: boolean }>> {
  return api.post<{ sourceId: string; enabled: boolean }>(
    `/external/sources/${encodeURIComponent(sourceId)}/enabled`,
    { enabled },
    { label: `${enabled ? 'ENABLE' : 'CUT'} FEED ${sourceId}` },
  );
}
